import { logger } from '@/lib/logger';

/**
 * The creator's own read/write access for one entity type, partitioned by
 * what each scoping association points AT.
 *
 * A scoping is a `(association type, target entity)` pair. `association_types.yaml`
 * declares each association's `target_entity_type_name`, which is what makes a
 * scoping public or private — NOT its name. Pairing an `ahoy_user`-targeted
 * association with the org entity (or vice versa) produces a scoping that can
 * never match a row; see `apps/orgs/entities/user_permission_resolver.py`.
 *
 * Public/Private is the same vocabulary the CSV-import defaults panel uses for
 * the same underlying association pair (`FileDefaultsPanel`).
 */
export interface ReadScopings {
  /** The creator holds an unscoped read: the whole type, no public/private split. */
  unrestricted: boolean;
  /** Scopings targeting the org entity — records visible to everyone in the org. */
  publicScopings: string[];
  /** Scopings targeting the user entity — the creator's private records. */
  privateScopings: string[];
}

/** Target entity type of a scoping association that means "public" (org-wide). */
export const PUBLIC_TARGET_ENTITY_TYPE = 'ahoy_org';
/** Target entity type of a scoping association that means "private to a user". */
export const PRIVATE_TARGET_ENTITY_TYPE = 'ahoy_user';

/**
 * Event types the "write" toggle in the picker offers. This is a CLIENT-ONLY
 * UI convention for what "write access" means in the picker — the backend
 * enforces write access via non-escalation (a write always requires the
 * matching read), not via this list.
 */
export const CREDENTIAL_WRITE_EVENT_TYPES = [
  'ahoy_object_created',
  'ahoy_property_updated',
  'ahoy_object_deleted',
  'ahoy_association_added',
  'ahoy_association_removed',
] as const;

/**
 * Raw permission entry as it may come back from the backend or be built by
 * the UI — accepts both camelCase and snake_case key variants.
 */
interface RawPermission {
  entityTypeName?: string;
  entity_type_name?: string;
  eventTypeName?: string;
  event_type_name?: string;
  scopingAssociationTypeName?: string;
  scoping_association_type_name?: string;
}

/** True when a permission entry is a READ entry for the given entity type. */
function isReadPermissionFor(
  permission: RawPermission,
  entityTypeName: string
): boolean {
  const permissionEntityType =
    permission.entityTypeName ?? permission.entity_type_name;
  if (permissionEntityType !== entityTypeName) {
    return false;
  }
  const eventTypeName = permission.eventTypeName ?? permission.event_type_name;
  return !eventTypeName || eventTypeName === 'read';
}

/**
 * Partition the creator's read scopings for one entity type into public and
 * private, using each scoping association's declared target entity type.
 *
 * Classification is by TARGET, never by name: `ahoy_note_to_visible_user`
 * (private) and `ahoy_note_visible_to_org` (public) differ by one word, so any
 * name-matching heuristic is one typo from granting org-wide reads. An
 * association whose target cannot be resolved is dropped — fail closed.
 */
export function getReadScopings(
  permissions: RawPermission[],
  entityTypeName: string,
  associationTargetTypes: ReadonlyMap<string, string>
): ReadScopings {
  const empty: ReadScopings = {
    unrestricted: false,
    publicScopings: [],
    privateScopings: [],
  };

  const reads = permissions.filter(permission =>
    isReadPermissionFor(permission, entityTypeName)
  );
  if (reads.length === 0) {
    return empty;
  }

  const scopingNames = reads.map(
    permission =>
      permission.scopingAssociationTypeName ??
      permission.scoping_association_type_name
  );

  // A read entry with NO scoping grants the entire type; it subsumes any
  // scoped entry alongside it ("unrestricted subsumes scoped").
  if (scopingNames.some(name => !name)) {
    return { ...empty, unrestricted: true };
  }

  const publicScopings: string[] = [];
  const privateScopings: string[] = [];
  for (const name of scopingNames) {
    if (!name) {
      continue;
    }
    const target = associationTargetTypes.get(name);
    if (target === PUBLIC_TARGET_ENTITY_TYPE) {
      publicScopings.push(name);
    } else if (target === PRIVATE_TARGET_ENTITY_TYPE) {
      privateScopings.push(name);
    }
  }
  return { unrestricted: false, publicScopings, privateScopings };
}

/**
 * The creator's own write access for one entity type and event type,
 * partitioned by what each scoping association's target is — mirrors
 * `ReadScopings`, but scoped to a single write event type rather than "read".
 */
export interface WriteScopings {
  eventTypeName: string;
  /** The creator holds an unscoped write of this event type: no public/private split. */
  unrestricted: boolean;
  /** Scopings targeting the org entity — records writable by everyone in the org. */
  publicScopings: string[];
  /** Scopings targeting the user entity — the creator's own private records. */
  privateScopings: string[];
}

/**
 * Partition the creator's write scopings for one entity type into public and
 * private, grouped by event type. Mirrors `getReadScopings`, but a WRITE entry
 * is one with a non-empty `event_type_name` that is also one of the
 * documented `CREDENTIAL_WRITE_EVENT_TYPES` — entries for any other write
 * event type (e.g. `ahoy_object_restored`/`ahoy_object_merged`, which
 * `ahoy_default` already grants on common types) are outside the picker's
 * documented scope and are rejected here rather than silently included.
 */
const CREDENTIAL_WRITE_EVENT_TYPE_SET: ReadonlySet<string> = new Set(
  CREDENTIAL_WRITE_EVENT_TYPES
);

/**
 * Write event types `ahoy_default` grants on common types that the picker
 * deliberately does not offer. Every admin holds these, so their rejection is
 * the expected steady state — logged at debug, not warn, or every slideout
 * open emits one warning per (type × event) with nothing for anyone to act on.
 * Anything outside this set is genuinely unexpected and still warns.
 */
const EXPECTED_NON_PICKER_WRITE_EVENT_TYPES: ReadonlySet<string> = new Set([
  'ahoy_object_restored',
  'ahoy_object_merged',
]);

export function getWriteScopings(
  permissions: RawPermission[],
  entityTypeName: string,
  associationTargetTypes: ReadonlyMap<string, string>
): WriteScopings[] {
  const writes = permissions.filter(permission => {
    const permissionEntityType =
      permission.entityTypeName ?? permission.entity_type_name;
    if (permissionEntityType !== entityTypeName) {
      return false;
    }
    const eventTypeName =
      permission.eventTypeName ?? permission.event_type_name;
    if (!eventTypeName) {
      return false;
    }
    if (!CREDENTIAL_WRITE_EVENT_TYPE_SET.has(eventTypeName)) {
      // Called as methods, never detached into a local: the logger's levels
      // read `this` (its buffer), so `const log = logger.debug; log(...)`
      // throws at runtime while passing every test that mocks the logger.
      const context = {
        component: 'api_access',
        entity_type_name: permissionEntityType,
        event_type_name: eventTypeName,
      };
      if (EXPECTED_NON_PICKER_WRITE_EVENT_TYPES.has(eventTypeName)) {
        logger.debug('Rejecting non-canonical write permission entry', context);
      } else {
        logger.warn('Rejecting non-canonical write permission entry', context);
      }
      return false;
    }
    return true;
  });

  const entriesByEventType = new Map<string, RawPermission[]>();
  for (const permission of writes) {
    const eventTypeName = (permission.eventTypeName ??
      permission.event_type_name) as string;
    const entries = entriesByEventType.get(eventTypeName) ?? [];
    entries.push(permission);
    entriesByEventType.set(eventTypeName, entries);
  }

  const result: WriteScopings[] = [];
  for (const [eventTypeName, entries] of entriesByEventType) {
    const scopingNames = entries.map(
      permission =>
        permission.scopingAssociationTypeName ??
        permission.scoping_association_type_name
    );

    if (scopingNames.some(name => !name)) {
      result.push({
        eventTypeName,
        unrestricted: true,
        publicScopings: [],
        privateScopings: [],
      });
      continue;
    }

    const publicScopings: string[] = [];
    const privateScopings: string[] = [];
    for (const name of scopingNames) {
      if (!name) {
        continue;
      }
      const target = associationTargetTypes.get(name);
      if (target === PUBLIC_TARGET_ENTITY_TYPE) {
        publicScopings.push(name);
      } else if (target === PRIVATE_TARGET_ENTITY_TYPE) {
        privateScopings.push(name);
      }
    }
    result.push({
      eventTypeName,
      unrestricted: false,
      publicScopings,
      privateScopings,
    });
  }
  return result;
}

/**
 * Whether the caller can READ the given entity type. A grant only shares read
 * access, so a write-only permission entry (e.g. `ahoy_property_updated`) must
 * NOT make a type grantable. Mirrors `getReadScopeForType`'s read semantics:
 * an entry counts only when it has no event type or an explicit `read`.
 */
export function hasReadPermissionForType(
  permissions: RawPermission[],
  entityTypeName: string
): boolean {
  return permissions.some(permission => {
    const permissionEntityType =
      permission.entityTypeName ?? permission.entity_type_name;
    if (permissionEntityType !== entityTypeName) {
      return false;
    }
    const eventTypeName =
      permission.eventTypeName ?? permission.event_type_name;
    return !eventTypeName || eventTypeName === 'read';
  });
}
